<template>
  <div>
    <base-modal
      :title="$t('message.2fa.select2FAmethod')"
      :subtitle="$t('message.2fa.active2FA')"
      hook-sufix="TwoFactorAuth"
      modal-width="600px"
      minHeight="200px"
      :hasHelper="{
        text: $t('message.2fa.knowMore'),
        link: helperLink,
      }"
      :showBackButton="isFromProfile"
      :onBack="handleBackToProfile"
      disableDefaultPadding
      twoFactorModal
      persistent
      :content-class="{
        'two-factor-auth-modal--required':
          isRequired2faFromOrganization && !isFromProfile,
      }"
    >
      <template #content>
        <v-divider></v-divider>
        <v-list class="pa-0">
          <v-list-item class="px-4" @click="handleAppAuth">
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <v-icon size="16" class="mr-4">mdi-cellphone</v-icon>
                <div class="d-flex flex-column">
                  <span class="body-bold-small">{{
                    $t('message.2fa.authenticationApp')
                  }}</span>
                  <span class="body-regular-small">{{
                    $t('message.2fa.useAuthenticationApp')
                  }}</span>
                </div>
              </v-list-item-title>
              <v-list-item-subtitle> </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <span class="d-flex align-center">
                <v-tag v-if="!hasAppMFA" type="neutral">{{
                  $t('message.2fa.recommend')
                }}</v-tag>
                <v-tag v-else type="success">{{
                  $t('message.pages.pandaAssist.ready')
                }}</v-tag>
                <v-icon v-if="!hasAppMFA">mdi-chevron-right</v-icon>
                <base-btn
                  v-else
                  icon
                  class="ml-2"
                  size="small"
                  type="danger"
                  @click.stop="handleAppAuth"
                >
                  <v-icon>mdi-trash-can-outline</v-icon></base-btn
                >
              </span>
            </v-list-item-action>
          </v-list-item>

          <v-divider></v-divider>

          <v-list-item class="px-4" @click="handleSmsAuth">
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <v-icon size="16" class="mr-4">mdi-email-outline</v-icon>
                <div class="d-flex flex-column">
                  <span class="body-bold-small">{{
                    $t('message.2fa.SMS')
                  }}</span>
                  <span class="body-regular-small">{{
                    $t('message.2fa.receiveBySMS')
                  }}</span>
                </div>
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <span class="d-flex align-center">
                <v-tag v-if="hasSmsMFA" type="success">{{
                  $t('message.pages.pandaAssist.ready')
                }}</v-tag>
                <v-icon v-if="!hasSmsMFA">mdi-chevron-right</v-icon>
                <base-btn
                  v-else
                  icon
                  class="ml-2"
                  size="small"
                  type="danger"
                  @click.stop="handleSmsAuth"
                >
                  <v-icon>mdi-trash-can-outline</v-icon></base-btn
                >
              </span>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </template>
    </base-modal>
    <two-factor-disable-modal
      v-if="showDisableModal"
      :onSuccess="handleDisableSuccess"
      @close="$root.$emit('closeTwoFactorDisableModal')"
      :typeDisable="typeDisable"
    />
  </div>
</template>

<script>
import BaseModal from '../BaseModal.vue'
import linkHelper from '@/utils/linkHelper.mixin.js'
import { mapState } from 'vuex'
import twoFactorAuthMixin from '../../utils/twoFactorAuth.mixin'
import twoFactorAuthApi from '../../services/twoFactorAuthApi'
import TwoFactorDisableModal from './TwoFactorDisableModal.vue'

export default {
  name: 'TwoFactorAuthModal',
  components: {
    BaseModal,
    TwoFactorDisableModal,
  },
  mixins: [linkHelper, twoFactorAuthMixin],
  props: {
    fromProfile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isFromProfile: false,
      isRequired2faFromOrganization: false,
      typeDisable: null,
      showDisableModal: false,
    }
  },
  computed: {
    ...mapState('user', ['userAllInfo']),
    helperLink() {
      return this.enableTwoFactorAuthenticationHelper()
    },
    hasAppMFA() {
      return this.userAllInfo?.mfa_methods?.includes('app')
    },
    hasSmsMFA() {
      return this.userAllInfo?.mfa_methods?.includes('sms')
    },
  },
  mounted() {
    this.$root.$on('2faRequiredFromOrganization', (isRequired) => {
      this.isRequired2faFromOrganization = isRequired
    })
  },
  methods: {
    async handleAppAuth() {
      if (this.hasAppMFA) {
        this.typeDisable = 'app'
        this.$root.$emit('closeTwoFactorAuth')
        this.$root.$emit('openTwoFactorDisableModal', {
          typeDisable: this.typeDisable,
          onSuccess: async () => {
            try {
              await twoFactorAuthApi.mfaDisable()
              const updatedUserInfo = {
                ...this.userAllInfo,
                mfa_methods: this.userAllInfo.mfa_methods.filter(
                  (method) => method !== 'app'
                ),
              }
              this.$store.commit('user/UPDATE_USER_ALLINFO', updatedUserInfo)
              return
            } catch (error) {
              console.error(error)
            }
          },
        })
        return
      }
      this.$root.$emit('closeTwoFactorAuth')
      this.$root.$emit('openAuthentication2faApp', {
        fromProfile: this.isFromProfile,
      })
    },
    handleSmsAuth() {
      if (this.hasSmsMFA) {
        this.typeDisable = 'sms'
        this.$root.$emit('closeTwoFactorAuth')
        this.$root.$emit('openTwoFactorDisableModal', {
          typeDisable: this.typeDisable,
          onSuccess: async () => {
            try {
              await twoFactorAuthApi.mfaDisable()
              const updatedUserInfo = {
                ...this.userAllInfo,
                mfa_methods: this.userAllInfo.mfa_methods.filter(
                  (method) => method !== 'sms'
                ),
              }
              this.$store.commit('user/UPDATE_USER_ALLINFO', updatedUserInfo)
              return
            } catch (error) {
              console.error(error)
            }
          },
        })
        return
      }
      this.$root.$emit('closeTwoFactorAuth')
      this.$root.$emit('openAuthentication2faSms', {
        fromProfile: this.isFromProfile,
      })
    },
    handleBackToProfile() {
      this.$root.$emit('closeTwoFactorAuth')
      this.$root.$emit('openMyProfileModal')
    },
    handleDisableSuccess() {
      this.mfaEnabled = false
      this.$root.$emit('updateMfaStatus', false)
    },
  },
  created() {
    this.$root.$on('openTwoFactorAuth', (data) => {
      if (data && data.fromProfile) {
        this.isFromProfile = true
        this.$root.$emit('openned2faFromProfile')
      }
    })
    this.$root.$on('updateMfaStatus', (status) => {
      this.mfaEnabled = status
    })
  },
  beforeDestroy() {
    this.$root.$off('openTwoFactorAuth')
    this.$root.$off('updateMfaStatus')
  },
}
</script>

<style lang="scss" scoped>
.v-list-item {
  cursor: pointer;

  &:hover {
    background-color: var(--v-neutral-95-lightest);
  }
}
</style>
<style lang="scss">
.two-factor-auth-modal--required {
  .v-toolbar__items {
    display: none;
  }
}
</style>
